<template>
    <AppMgrPermissionAppList v-model="listSetting"
                             listTitle="利用を許可するアプリケーション（ホワイトリスト）"
                             idName="wlwl"
                             :mode=0
                             :getURLFunction="getURLFunction"
                             :getAppFunction="getAppFunction"
                             @notifyUpdate="onNotifyUpdate"
                             @notifyMDMApp="onNotifyMDMApp"
                             :isCheckMobileAndroid="!isCheckMobileAndroid"
                             :mdmAppPackageName="mdmAppPackageName"
                             :enableAddMDMApp="enableAddMDMApp"
                             :getUntrustedAppsPolicyFunction="getUntrustedAppsPolicyFunction"
                             :version ="version">
    </AppMgrPermissionAppList>
</template>

<script lang="ts">
    import { Options, Vue } from "vue-class-component";
    import { cloneDeep } from 'lodash';
    import AppMgrPermissionAppList from '@/components/Mobile/AndroidAppDist/AppMgrPermissionAppList.vue'
    import { AndroidAppMgrApplicationSetting, AndroidAppMgrApplicationBasicJson } from '@/models/Mobile/AndroidAppDist/AndroidAppMgrApplicationSetting';
    import { AndroidPolicyWrapper } from '@/models/Mobile/Android/AndroidPolicyWrapper'
    import * as schema from '@/models/Mobile/Android/AndroidManagementV1Schema'
    import { ApplicationPolicyConverter, ApplicationPolicyDef } from '@/models/Mobile/AndroidAppDist/ApplicationPolicyConverter'
    import { AndroidAppDistDefinition } from "@/components/Mobile/AndroidAppDist/AndroidAppDistDefinition";

    @Options({
        props: {
            getURLFunction: { type: Function },     // Google Play を iframe に埋め込むためのURL取得関数
            getAppFunction: { type: Function },      // アプリケーション詳細情報の取得関数
            isCheckMobileAndroid: { type: Boolean, default: false }, // AndroidライセンスがONかどうか
            getUntrustedAppsPolicyFunction: { type: Function }      // 提供元不明のアプリケーションのインストール
        },
        components: {
            AppMgrPermissionAppList
        },
        emits: ['onNotifyUpdate'],
    })

    export default class AppMgrWhiteListSettingForAndroid extends Vue {
        // 設定データ
        private listSetting = new Array<AndroidAppMgrApplicationSetting>();

        // コンバータ
        private converter = new ApplicationPolicyConverter();
        
        // MDMアプリ情報
        private mdmAppPackageName = ""; // パッケージ名
        private isMDMAppInstall = 0;    // インストール設定
        private enableAddMDMApp = true; // MDMアプリの追加をおこなうかどうか
        private version = 0;

        /**
         * MDMアプリの登録状態変更通知
         */
        private onNotifyMDMApp(isMDM: boolean): void {
            // 手動でインストール設定の時のみ更新
            if (2 == this.isMDMAppInstall) {
                this.enableAddMDMApp = !isMDM;
            }
        }

        /**
         * 更新通知
         */
        private onNotifyUpdate(): void {
            this.$emit("onNotifyUpdate");
        }

        /**
         * UIデータの初期化
         */
        public InitData(editPolicy: AndroidPolicyWrapper): void {
            this.listSetting = new Array<AndroidAppMgrApplicationSetting>();
            // オンプレが過去管理機の場合を考慮して一旦初期値を設定
            this.isMDMAppInstall = 0;
            this.mdmAppPackageName = "";
            this.enableAddMDMApp = false;
            this.version = 0;

            if (undefined != editPolicy.skypolicy.skyAppInfo) {
                this.mdmAppPackageName = editPolicy.skypolicy.skyAppInfo.mdmAppInfo.packageName;
            }
            if (undefined != editPolicy.skypolicy.settingInfo) {
                this.isMDMAppInstall = editPolicy.skypolicy.settingInfo.mdmAppInstallType;
            }
            if (undefined != editPolicy.skypolicy.version) {
                this.version = editPolicy.skypolicy.version;
            }

            // 手動でインストールかどうかで初期値を設定
            if (2 == this.isMDMAppInstall) {
                this.enableAddMDMApp = true;
            }
            else {
                this.enableAddMDMApp = false;
            }
        }

        /**
         * UIデータの反映
         */
        public LoadUIData(editPolicy: AndroidPolicyWrapper): void {
            this.InitData(editPolicy);
            if (undefined == editPolicy.policy.applications) {
                return;
            }

            // 基本情報の取得が必要なパッケージ
            const packageNames = new Array<string>();
            // 基本情報を紐づけしやすいようにMapにも記憶
            const packageMap = new Map<string, AndroidAppMgrApplicationSetting>();

            for (const policy of editPolicy.policy.applications) {
                const setting = new AndroidAppMgrApplicationSetting();
                setting.policyJson = policy;
                setting.basicInfo.packageName = this.converter.GetPackageName(setting.policyJson);  // 基本情報が取れていないとbasicInfoにパッケージIDがない状態になり、色々と都合が悪いのでここで保管しておく
                                
                if(policy.appNameforScv){
                    setting.basicInfo.appSettingType = AndroidAppDistDefinition.APP_SETTING_TYPE_FREE;
                    setting.basicInfo.appName = policy.appNameforScv;
                }
                else setting.basicInfo.appSettingType = AndroidAppDistDefinition.APP_SETTING_TYPE_GOOGLE_PLAY;
                if (ApplicationPolicyDef.DISTRIBUTION_TYPE.Black != this.converter.GetDistributionType(setting.policyJson)) {
                    // 許可アプリ
                    this.listSetting.push(setting);

                    // 手動でインストールかつ、MDMアプリが登録されてる
                    if (2 == this.isMDMAppInstall && this.mdmAppPackageName == setting.policyJson.packageName) {
                        this.enableAddMDMApp = false;
                    }
                }

                packageNames.push(this.converter.GetPackageName(setting.policyJson));
                packageMap.set(this.converter.GetPackageName(setting.policyJson), setting);
            }

            // アプリの基本情報を割り当て
            if (undefined != editPolicy.skypolicy.applicationInfo) {
                for (let i = 0; i < editPolicy.skypolicy.applicationInfo.length; i++) {
                    const setting = packageMap.get(editPolicy.skypolicy.applicationInfo[i].packageName);
                    if (undefined == setting) {
                        continue;
                    }

                    if(setting.basicInfo.appSettingType === AndroidAppDistDefinition.APP_SETTING_TYPE_GOOGLE_PLAY){
                        setting.basicInfo = {
                            appSettingType:AndroidAppDistDefinition.APP_SETTING_TYPE_GOOGLE_PLAY,
                            ...cloneDeep(editPolicy.skypolicy.applicationInfo[i])
                        }
                    }
                    // 基本情報の更新日時はundefinedにする。更新されたデータだけ送信したいので。
                    setting.basicInfo.updateTimeUtc = undefined;
                }
            }
        }

        /**
         * UIデータの吸い上げ
         */
        public SaveUIData(editPolicy: AndroidPolicyWrapper): void {
            editPolicy.policy.applications = new Array<schema.AMAPI_ApplicationPolicyExtend>();

            let bCreateApplicationInfo = false;
            if (undefined == editPolicy.skypolicy.applicationInfo) {
                editPolicy.skypolicy.applicationInfo = new Array<AndroidAppMgrApplicationBasicJson>();
                bCreateApplicationInfo = true;
            }

            // #45289 【M1クラウド（Android端末のMDM対応）】アプリ管理で設定を切り替えた後、Android端末に設定が下りてこない
            // リスト内に同じパッケージが複数あると、設定が適用できないなどの不具合に繋がる
            // ただ、リスト内で重複しているパッケージを作成させる方法がわからなかった。
            // フェールセーフとして、重複チェックを行い先勝ちで実装する。
            const setDuplication = new Set<string>();
            const arDeleteDuplicationIndex = new Array<number>();

            for (let i = 0; i < this.listSetting.length; i++) {
                const item = this.listSetting[i];
                const lowerPackage = this.converter.GetPackageName(item.policyJson).toLowerCase();
                if (!setDuplication.has(lowerPackage)) {
                    setDuplication.add(lowerPackage);
                    editPolicy.policy.applications.push(item.policyJson);

                    // 更新された基本情報
                    if (undefined != item.basicInfo.updateTimeUtc && item.basicInfo.appSettingType === AndroidAppDistDefinition.APP_SETTING_TYPE_GOOGLE_PLAY) {
                        editPolicy.skypolicy.applicationInfo.push(item.basicInfo as AndroidAppMgrApplicationBasicJson);
                    }
                }
                else {
                    arDeleteDuplicationIndex.push(i);
                }
            }

            // 重複していたパッケージを削除
            for (let i = arDeleteDuplicationIndex.length; 0 < i; i--) {
                this.listSetting.splice(arDeleteDuplicationIndex[i - 1], 1);
            }

            if (0 == editPolicy.skypolicy.applicationInfo.length) {
                if (bCreateApplicationInfo) {
                    // applicationInfoはここで作ったものなので削除する
                    editPolicy.skypolicy.applicationInfo = undefined;
                }
            }
        }
    }
</script>

<style>
    /* アプリケーション管理部分のCSSはオンプレ版と共通を使用する */
    @import "../../../assets/css/MobileAndroidCommon.css";
</style>